import React from 'react';
import {
  STANDARD_BOT_URL,
  BOT_SPECTRA,
  FEDERATION_INTERNAL_BOTS,
} from '../data/federationAssets';

/**
 * FederationBotRoster — SFUI-004 R3 living index.
 *
 * Surfaces all spectral classes side-by-side so a user can recognize
 * any bot in the system at a glance.  The base spectral classes
 * (blue / purple / red / gold) render the canonical winged-bot model
 * with the appropriate CSS filter; named internal bots (e.g.,
 * The Recycler) bring their own portrait but still declare a spectrum
 * so the chrome around them matches the system colour code.
 */

const SPECTRUM_USAGES = {
  blue: 'Tour Guide · Operator clerks · sibling welcome',
  purple: 'Library reference · records · archival lookup',
  red: 'Restricted · Truth Gate · governance ops',
  gold: 'Token redemption · Gumroad bridge · commerce',
  magenta: 'Entropy · node lifecycle · renewal cycles',
  emerald: 'Mirror · storage symmetry · data integrity',
};

export default function FederationBotRoster() {
  // Base classes that render the standard bot model with a filter
  const baseClasses = ['blue', 'purple', 'red', 'gold', 'emerald'].map((spectrum) => ({
    kind: 'base',
    spectrum,
    avatar: STANDARD_BOT_URL,
    avatarAlt: `${BOT_SPECTRA[spectrum].label} bot — NextXus Federation`,
    name: BOT_SPECTRA[spectrum].label,
    role: SPECTRUM_USAGES[spectrum],
    publicLink: null,
    statusLabel: 'Base spectral model',
  }));

  // Named internal bots
  const namedBots = FEDERATION_INTERNAL_BOTS.map((b) => ({
    kind: 'named',
    spectrum: b.spectrum,
    avatar: b.avatar,
    avatarAlt: b.avatarAlt,
    name: b.name,
    role: b.role,
    publicLink: b.publicLink,
    statusLabel: b.statusLabel,
    noFilter: Boolean(b.noFilter),
  }));

  const all = [...baseClasses, ...namedBots];

  return (
    <section
      data-testid="federation-bot-roster"
      aria-label="Federation Bot Roster — SFUI-004 R3"
      className="container mx-auto px-4 py-16"
    >
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-amber-300/80 mb-2">
          SFUI-004 R3 · Bot Differentiation
        </div>
        <h2 className="text-2xl md:text-4xl font-extrabold text-white tracking-tight">
          Federation <span className="text-amber-300">Bot Roster</span>
        </h2>
        <p className="mt-3 text-gray-300 text-sm md:text-base leading-relaxed">
          One canonical base, five spectral classes. Every bot in the lattice carries
          a colour code so its role is identifiable at a glance — no placeholder icons,
          no custom one-offs.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {all.map((bot) => {
          const spec = BOT_SPECTRA[bot.spectrum] || BOT_SPECTRA.blue;
          return (
            <div
              key={`${bot.kind}-${bot.spectrum}-${bot.name}`}
              data-testid={`bot-roster-${bot.spectrum}${bot.kind === 'named' ? '-named' : ''}`}
              className="rounded-2xl border bg-black/60 backdrop-blur-sm p-4 text-center transition-transform hover:-translate-y-0.5"
              style={{
                borderColor: `${spec.accent}55`,
                boxShadow: `0 0 18px ${spec.accent}1f`,
              }}
            >
              <div className="flex justify-center mb-3">
                <span
                  className="inline-block rounded-full overflow-hidden"
                  style={{
                    width: '88px',
                    height: '88px',
                    border: `2px solid ${spec.accent}`,
                    boxShadow: `0 0 18px ${spec.accent}66`,
                    background: '#000',
                  }}
                >
                  <img
                    src={bot.avatar}
                    alt={bot.avatarAlt}
                    loading="lazy"
                    decoding="async"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      filter: bot.noFilter ? 'none' : spec.filter,
                      display: 'block',
                    }}
                  />
                </span>
              </div>
              <div
                className="text-[10px] font-mono uppercase tracking-[0.18em] mb-1"
                style={{ color: spec.accent }}
              >
                {bot.spectrum} · {BOT_SPECTRA[bot.spectrum].label}
              </div>
              <div className="text-sm font-bold text-white mb-1 leading-tight">{bot.name}</div>
              <div className="text-[11px] text-gray-300/85 leading-snug min-h-[2.5em]">
                {bot.role}
              </div>
              <div className="mt-3 text-[9px] font-mono uppercase tracking-[0.18em] text-gray-500">
                {bot.publicLink ? (
                  <a
                    href={bot.publicLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-gray-300"
                  >
                    {bot.statusLabel} ↗
                  </a>
                ) : (
                  bot.statusLabel
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
